"use client";

import { ArrowRight, Building2, ChevronLeft, ChevronRight, Github, GraduationCap, Linkedin, Link as LinkIcon, Loader2, User } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/components/auth/auth-provider";
import { TopHeader } from "@/components/nav/top-header";
import { adminFetchGithub, adminFetchLinkedin } from "@/lib/api/admin-enrichment";
import { ApiClientError } from "@/lib/api/client";
import type { CompanyLookupResult, EducationLookupResult, NameLookupResult } from "@/lib/api/profiles";
import { lookupByCompany, lookupByEducation, lookupByName, lookupGithub, lookupLinkedin } from "@/lib/api/profiles";

type ProfileSource = "github" | "linkedin";

interface LookupResult {
  status: "found_from_db" | "fetched_live" | "not_found" | "rate_limited" | "generic_error";
  candidate_id: number | null | undefined;
  display_name: string | null | undefined;
  identifier: string | null | undefined;
  message: string | null | undefined;
  source: ProfileSource;
}

type LookupState =
  | { phase: "idle" }
  | { phase: "loading"; source: ProfileSource | "name" }
  | { phase: "fetching"; source: ProfileSource }
  | { phase: "result"; response: LookupResult }
  | { phase: "name_results"; results: NameLookupResult[]; query: string; totalCount: number | null; page: number }
  | { phase: "error"; message: string };

const PAGE_SIZE = 15;

/**
 * Detect whether a URL is GitHub or LinkedIn, and extract the identifier.
 * Returns null if the URL doesn't match either pattern.
 */
function detectUrlSource(input: string): { source: ProfileSource; identifier: string } | null {
  const trimmed = input.trim();

  // GitHub URL
  const ghMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?github\.com\/([a-zA-Z0-9_-]+)\/?$/
  );
  if (ghMatch?.[1]) {
    return { source: "github", identifier: ghMatch[1] };
  }

  // LinkedIn URL
  const liMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?(?:[a-z]{2}\.)?linkedin\.com\/in\/([a-zA-Z0-9._%-]+)\/?(?:\?.*)?$/
  );
  if (liMatch?.[1]) {
    return { source: "linkedin", identifier: liMatch[1].toLowerCase() };
  }

  return null;
}

function getStatusLabel(status: LookupResult["status"]): string {
  switch (status) {
    case "found_from_db":
      return "Found in database";
    case "fetched_live":
      return "Fetched live";
    case "not_found":
      return "Not found";
    case "rate_limited":
      return "Rate limited";
    case "generic_error":
      return "Error";
    default:
      return "Unknown status";
  }
}

function getStatusDescription(response: LookupResult, isAdmin: boolean): string {
  const name = response.display_name ?? response.identifier ?? "This profile";
  const sourceName = response.source === "github" ? "GitHub" : "LinkedIn";

  switch (response.status) {
    case "found_from_db":
      return `${name} is already tracked. Redirecting to profile...`;
    case "fetched_live":
      return `${name} has been fetched from ${sourceName} and added. Redirecting to profile...`;
    case "not_found":
      if (isAdmin) {
        return response.message ?? `Not found in database. Use Fetch from ${sourceName} to import this profile.`;
      }
      return `This ${sourceName} profile is not in our database.`;
    case "rate_limited":
      return response.message ?? `${sourceName} rate limit reached. Please wait a few minutes and try again.`;
    case "generic_error":
      return response.message ?? "An unexpected error occurred during the lookup. Please try again.";
    default:
      return "Unexpected response from the server.";
  }
}

function isSuccessStatus(status: LookupResult["status"]): boolean {
  return status === "found_from_db" || status === "fetched_live";
}

function getStatusColor(status: LookupResult["status"]): string {
  if (isSuccessStatus(status)) return "#74d39d";
  if (status === "not_found") return "var(--text-secondary)";
  if (status === "rate_limited") return "var(--accent-yellow)";
  return "#ef8f8f";
}

function SourceIcon({ source, size = 15 }: { source: ProfileSource; size?: number }) {
  return source === "github" ? <Github size={size} /> : <Linkedin size={size} />;
}

export default function LookupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAdmin } = useAuth();
  const [input, setInput] = useState(searchParams.get("q") ?? "");
  const [state, setState] = useState<LookupState>({ phase: "idle" });
  const autoTriggered = useRef(false);

  // Company search state
  const [companyInput, setCompanyInput] = useState("");
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyResults, setCompanyResults] = useState<CompanyLookupResult[] | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [companyTotal, setCompanyTotal] = useState<number | null>(null);
  const [companyPage, setCompanyPage] = useState(0);

  // Education search state
  const [eduInput, setEduInput] = useState("");
  const [eduLoading, setEduLoading] = useState(false);
  const [eduResults, setEduResults] = useState<EducationLookupResult[] | null>(null);
  const [eduError, setEduError] = useState<string | null>(null);
  const [eduTotal, setEduTotal] = useState<number | null>(null);
  const [eduPage, setEduPage] = useState(0);

  const handleAdminFetch = useCallback(async () => {
    const detected = detectUrlSource(input);
    if (!detected) return;

    setState({ phase: "fetching", source: detected.source });

    try {
      if (detected.source === "github") {
        const response = await adminFetchGithub(detected.identifier);
        const result: LookupResult = {
          status: response.status,
          candidate_id: response.candidate_id,
          display_name: response.display_name,
          identifier: response.github_login,
          message: response.message,
          source: "github",
        };
        setState({ phase: "result", response: result });

        if (isSuccessStatus(result.status) && result.candidate_id) {
          setTimeout(() => {
            router.push(`/profile/${result.candidate_id}?tab=technical&returnTo=/lookup`);
          }, 1200);
        }
      } else {
        const response = await adminFetchLinkedin(detected.identifier);
        const result: LookupResult = {
          status: response.status,
          candidate_id: response.candidate_id,
          display_name: response.display_name,
          identifier: response.linkedin_handle,
          message: response.message,
          source: "linkedin",
        };
        setState({ phase: "result", response: result });

        if (isSuccessStatus(result.status) && result.candidate_id) {
          setTimeout(() => {
            router.push(`/profile/${result.candidate_id}?tab=experience&returnTo=/lookup`);
          }, 1200);
        }
      }
    } catch (error) {
      setState({
        phase: "error",
        message: error instanceof Error ? error.message : "Fetch failed. Please try again.",
      });
    }
  }, [input, router]);

  const handleLookup = useCallback(async () => {
    const trimmedInput = input.trim();

    // Numeric input -> navigate directly to profile
    if (/^\d+$/.test(trimmedInput)) {
      router.push(`/profile/${trimmedInput}?returnTo=/lookup`);
      return;
    }

    const detected = detectUrlSource(trimmedInput);

    if (!detected) {
      // Not numeric, not a URL -> try name search
      setState({ phase: "loading", source: "name" });
      try {
        const nameResponse = await lookupByName(trimmedInput, PAGE_SIZE, 0);
        if (nameResponse.status === "found" && nameResponse.results.length > 0) {
          if (nameResponse.results.length === 1 && (nameResponse.total_count ?? 1) === 1) {
            // Single match -- navigate directly
            router.push(`/profile/${nameResponse.results[0].candidate_id}?returnTo=/lookup`);
            return;
          }
          setState({ phase: "name_results", results: nameResponse.results, query: trimmedInput, totalCount: nameResponse.total_count, page: 0 });
        } else {
          setState({
            phase: "error",
            message: nameResponse.message || `No matches for "${trimmedInput}".`,
          });
        }
      } catch (error) {
        setState({
          phase: "error",
          message: error instanceof Error ? error.message : "Name lookup failed.",
        });
      }
      return;
    }

    setState({ phase: "loading", source: detected.source });

    try {
      if (detected.source === "github") {
        const response = await lookupGithub(detected.identifier);
        const result: LookupResult = {
          status: response.status,
          candidate_id: response.candidate_id,
          display_name: response.display_name,
          identifier: response.github_login,
          message: response.message,
          source: "github",
        };
        setState({ phase: "result", response: result });

        if (isSuccessStatus(result.status) && result.candidate_id) {
          setTimeout(() => {
            router.push(`/profile/${result.candidate_id}?tab=technical&returnTo=/lookup`);
          }, 1200);
        }
      } else {
        const response = await lookupLinkedin(detected.identifier);
        const result: LookupResult = {
          status: response.status,
          candidate_id: response.candidate_id,
          display_name: response.display_name,
          identifier: response.linkedin_handle,
          message: response.message,
          source: "linkedin",
        };
        setState({ phase: "result", response: result });

        if (isSuccessStatus(result.status) && result.candidate_id) {
          setTimeout(() => {
            router.push(`/profile/${result.candidate_id}?tab=experience&returnTo=/lookup`);
          }, 1200);
        }
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        setState({
          phase: "error",
          message: `Request failed (${error.status}). Please try again.`,
        });
      } else {
        setState({
          phase: "error",
          message: error instanceof Error
            ? error.message
            : "Network error. Please check your connection and try again.",
        });
      }
    }
  }, [input, router]);

  // Auto-trigger lookup when arriving with ?q= param (e.g. after login redirect)
  useEffect(() => {
    if (autoTriggered.current) return;
    const q = searchParams.get("q");
    if (q) {
      autoTriggered.current = true;
      handleLookup();
    }
  }, [searchParams, handleLookup]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleLookup();
    }
  };

  const [namePageLoading, setNamePageLoading] = useState(false);
  const handleNamePageChange = useCallback(async (newPage: number) => {
    if (state.phase !== "name_results") return;
    const q = state.query;
    setNamePageLoading(true);
    try {
      const nameResponse = await lookupByName(q, PAGE_SIZE, newPage * PAGE_SIZE);
      if (nameResponse.status === "found" && nameResponse.results.length > 0) {
        setState({ phase: "name_results", results: nameResponse.results, query: q, totalCount: nameResponse.total_count, page: newPage });
      }
    } catch {
      setState({ phase: "error", message: "Page load failed." });
    } finally {
      setNamePageLoading(false);
    }
  }, [state]);

  const handleCompanySearch = useCallback(async () => {
    const trimmed = companyInput.trim();
    if (!trimmed || trimmed.length < 2) return;

    setCompanyLoading(true);
    setCompanyError(null);
    setCompanyResults(null);
    setCompanyTotal(null);
    setCompanyPage(0);

    try {
      const response = await lookupByCompany(trimmed, PAGE_SIZE, 0);
      if (response.status === "found" && response.results.length > 0) {
        setCompanyResults(response.results);
        setCompanyTotal(response.total_count);
      } else {
        setCompanyError(response.message || `No matches for "${trimmed}".`);
      }
    } catch (error) {
      setCompanyError(error instanceof Error ? error.message : "Company lookup failed.");
    } finally {
      setCompanyLoading(false);
    }
  }, [companyInput]);

  const handleCompanyPageChange = useCallback(async (newPage: number) => {
    const trimmed = companyInput.trim();
    if (!trimmed) return;

    setCompanyLoading(true);
    try {
      const response = await lookupByCompany(trimmed, PAGE_SIZE, newPage * PAGE_SIZE);
      if (response.status === "found" && response.results.length > 0) {
        setCompanyResults(response.results);
        setCompanyTotal(response.total_count);
        setCompanyPage(newPage);
      }
    } catch (error) {
      setCompanyError(error instanceof Error ? error.message : "Page load failed.");
    } finally {
      setCompanyLoading(false);
    }
  }, [companyInput]);

  const handleCompanyKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleCompanySearch();
    }
  };

  const handleEducationSearch = useCallback(async () => {
    const trimmed = eduInput.trim();
    if (!trimmed || trimmed.length < 2) return;

    setEduLoading(true);
    setEduError(null);
    setEduResults(null);
    setEduTotal(null);
    setEduPage(0);

    try {
      const response = await lookupByEducation(trimmed, PAGE_SIZE, 0);
      if (response.status === "found" && response.results.length > 0) {
        setEduResults(response.results);
        setEduTotal(response.total_count);
      } else {
        setEduError(response.message || `No matches for "${trimmed}".`);
      }
    } catch (error) {
      setEduError(error instanceof Error ? error.message : "Education lookup failed.");
    } finally {
      setEduLoading(false);
    }
  }, [eduInput]);

  const handleEduPageChange = useCallback(async (newPage: number) => {
    const trimmed = eduInput.trim();
    if (!trimmed) return;

    setEduLoading(true);
    try {
      const response = await lookupByEducation(trimmed, PAGE_SIZE, newPage * PAGE_SIZE);
      if (response.status === "found" && response.results.length > 0) {
        setEduResults(response.results);
        setEduTotal(response.total_count);
        setEduPage(newPage);
      }
    } catch (error) {
      setEduError(error instanceof Error ? error.message : "Page load failed.");
    } finally {
      setEduLoading(false);
    }
  }, [eduInput]);

  const handleEduKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleEducationSearch();
    }
  };

  const isLoading = state.phase === "loading" || state.phase === "fetching";
  const sourceName =
    state.phase === "loading" || state.phase === "fetching"
      ? state.source === "github" ? "GitHub" : state.source === "linkedin" ? "LinkedIn" : "name"
      : "";

  return (
    <>
      <TopHeader />
      <main
        className="page-container al-fade-up"
        style={{
          paddingTop: "6.5rem",
          paddingBottom: "2rem",
          display: "grid",
          gap: "1rem",
          maxWidth: "640px",
          margin: "0 auto",
        }}
      >
        <div style={{ display: "grid", gap: "0.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <LinkIcon size={20} />
            <h1
              style={{
                margin: 0,
                fontFamily: '"AlisterHero", "AlisterSourceSans", sans-serif',
                fontSize: "1.3rem",
              }}
            >
              Profile Lookup
            </h1>
          </div>
          <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.5 }}>
            Enter a name or GitHub/LinkedIn URL.
          </p>
        </div>

        <section
          className="surface-card"
          style={{ padding: "0.85rem", display: "flex", gap: "0.5rem" }}
        >
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. Vojtech Sykora, github.com/sykoravojtech, or linkedin.com/in/sykoravojtech"
            disabled={isLoading}
            style={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid var(--border)",
              background: "var(--surface)",
              color: "var(--text-primary)",
              padding: "0.6rem 0.7rem",
            }}
          />
          <button
            type="button"
            onClick={handleLookup}
            disabled={isLoading || !input.trim()}
            style={{
              borderRadius: "10px",
              border: "1px solid var(--border)",
              background: "var(--surface-elevated)",
              color: "var(--text-primary)",
              padding: "0.6rem 0.85rem",
              cursor: isLoading || !input.trim() ? "not-allowed" : "pointer",
              opacity: isLoading || !input.trim() ? 0.5 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
            }}
          >
            {state.phase === "loading" ? (
              <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
            ) : (
              <ArrowRight size={16} />
            )}
          </button>
        </section>

        {isLoading && (
          <section
            className="surface-card"
            style={{
              padding: "1.2rem",
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
            }}
          >
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite", color: "var(--text-secondary)" }} />
            <span style={{ color: "var(--text-secondary)" }}>
              {state.phase === "fetching"
                ? `Fetching from ${sourceName}...`
                : sourceName === "name"
                  ? "Searching by name..."
                  : `Looking up ${sourceName} profile...`}
            </span>
          </section>
        )}

        {state.phase === "result" && (
          <section className="surface-card" style={{ padding: "1.2rem", display: "grid", gap: "0.6rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: getStatusColor(state.response.status),
                }}
              />
              <strong>{getStatusLabel(state.response.status)}</strong>
              <SourceIcon source={state.response.source} size={14} />
            </div>

            {state.response.display_name && (
              <p style={{ margin: 0 }}>
                <strong>{state.response.display_name}</strong>
                {state.response.identifier && (
                  <span style={{ color: "var(--text-secondary)" }}>
                    {" "}({state.response.source === "github" ? "@" : ""}{state.response.identifier})
                  </span>
                )}
              </p>
            )}

            <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.55 }}>
              {getStatusDescription(state.response, isAdmin)}
            </p>

            {!isSuccessStatus(state.response.status) && (
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {isAdmin && state.response.status === "not_found" && (
                  <button
                    type="button"
                    onClick={handleAdminFetch}
                    style={{
                      width: "fit-content",
                      borderRadius: "10px",
                      border: "1px solid color-mix(in srgb, var(--accent-blue) 42%, var(--border) 58%)",
                      background: "color-mix(in srgb, var(--accent-blue) 16%, var(--surface) 84%)",
                      color: "color-mix(in srgb, var(--accent-blue) 84%, #fff 16%)",
                      padding: "0.45rem 0.75rem",
                      cursor: "pointer",
                      fontSize: "0.88rem",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.35rem",
                    }}
                  >
                    <SourceIcon source={state.response.source} />
                    Fetch from {state.response.source === "github" ? "GitHub" : "LinkedIn"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setState({ phase: "idle" })}
                  style={{
                    width: "fit-content",
                    borderRadius: "10px",
                    border: "1px solid var(--border)",
                    background: "var(--surface-elevated)",
                    color: "var(--text-primary)",
                    padding: "0.45rem 0.75rem",
                    cursor: "pointer",
                    fontSize: "0.88rem",
                  }}
                >
                  Try again
                </button>
              </div>
            )}
          </section>
        )}

        {state.phase === "name_results" && (
          <section className="surface-card" style={{ padding: "1.2rem", display: "grid", gap: "0.6rem", opacity: namePageLoading ? 0.6 : 1, transition: "opacity 0.15s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: "#74d39d",
                }}
              />
              <strong>
                {state.totalCount != null && state.totalCount > PAGE_SIZE
                  ? `Showing ${state.page * PAGE_SIZE + 1}\u2013${Math.min((state.page + 1) * PAGE_SIZE, state.totalCount)} of ${state.totalCount}`
                  : `${state.results.length} match${state.results.length !== 1 ? "es" : ""}`}
              </strong>
              <span style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>
                for &ldquo;{state.query}&rdquo;
              </span>
            </div>

            <div style={{ display: "grid", gap: "0.35rem" }}>
              {state.results.map((r) => (
                <button
                  key={r.candidate_id}
                  type="button"
                  onClick={() => router.push(`/profile/${r.candidate_id}?returnTo=/lookup`)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.65rem 0.75rem",
                    borderRadius: "10px",
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  <User size={16} style={{ color: "var(--text-secondary)" }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.display_name}
                    </div>
                    {(r.primary_company || r.primary_headline) && (
                      <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.primary_headline || r.primary_company}
                      </div>
                    )}
                  </div>
                  <ArrowRight size={14} style={{ color: "var(--text-secondary)" }} />
                </button>
              ))}
            </div>

            {state.totalCount != null && state.totalCount > PAGE_SIZE && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", paddingTop: "0.4rem" }}>
                <button
                  type="button"
                  disabled={state.page === 0 || namePageLoading}
                  onClick={() => handleNamePageChange(state.page - 1)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.25rem",
                    padding: "0.35rem 0.6rem", borderRadius: "8px",
                    border: "1px solid var(--border)", background: "var(--surface)",
                    color: state.page === 0 ? "var(--text-secondary)" : "var(--text-primary)",
                    cursor: state.page === 0 || namePageLoading ? "not-allowed" : "pointer",
                    opacity: state.page === 0 ? 0.4 : 1, fontSize: "0.82rem",
                  }}
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                  {state.page + 1} / {Math.ceil(state.totalCount / PAGE_SIZE)}
                </span>
                <button
                  type="button"
                  disabled={(state.page + 1) * PAGE_SIZE >= state.totalCount || namePageLoading}
                  onClick={() => handleNamePageChange(state.page + 1)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.25rem",
                    padding: "0.35rem 0.6rem", borderRadius: "8px",
                    border: "1px solid var(--border)", background: "var(--surface)",
                    color: (state.page + 1) * PAGE_SIZE >= state.totalCount ? "var(--text-secondary)" : "var(--text-primary)",
                    cursor: (state.page + 1) * PAGE_SIZE >= state.totalCount || namePageLoading ? "not-allowed" : "pointer",
                    opacity: (state.page + 1) * PAGE_SIZE >= state.totalCount ? 0.4 : 1, fontSize: "0.82rem",
                  }}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </section>
        )}

        {state.phase === "error" && (
          <section
            className="surface-card"
            style={{
              padding: "1.2rem",
              display: "grid",
              gap: "0.6rem",
              borderColor: "color-mix(in srgb, var(--accent-red) 30%, var(--border) 70%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: "#ef8f8f",
                }}
              />
              <strong>Request failed</strong>
            </div>
            <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.55 }}>
              {state.message}
            </p>
            <button
              type="button"
              onClick={() => setState({ phase: "idle" })}
              style={{
                width: "fit-content",
                borderRadius: "10px",
                border: "1px solid var(--border)",
                background: "var(--surface-elevated)",
                color: "var(--text-primary)",
                padding: "0.45rem 0.75rem",
                cursor: "pointer",
                fontSize: "0.88rem",
              }}
            >
              Try again
            </button>
          </section>
        )}
        {/* Company Search Section */}
        <div style={{ display: "grid", gap: "0.5rem", marginTop: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Building2 size={20} />
            <h2
              style={{
                margin: 0,
                fontFamily: '"AlisterHero", "AlisterSourceSans", sans-serif',
                fontSize: "1.15rem",
              }}
            >
              Company Search
            </h2>
          </div>
          <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.5, fontSize: "0.9rem" }}>
            Find people who work (or worked) at a specific company.
          </p>
        </div>

        <section
          className="surface-card"
          style={{ padding: "0.85rem", display: "flex", gap: "0.5rem" }}
        >
          <input
            value={companyInput}
            onChange={(event) => setCompanyInput(event.target.value)}
            onKeyDown={handleCompanyKeyDown}
            placeholder="e.g. ElevenLabs, Google, Stripe"
            disabled={companyLoading}
            style={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid var(--border)",
              background: "var(--surface)",
              color: "var(--text-primary)",
              padding: "0.6rem 0.7rem",
            }}
          />
          <button
            type="button"
            onClick={handleCompanySearch}
            disabled={companyLoading || !companyInput.trim() || companyInput.trim().length < 2}
            style={{
              borderRadius: "10px",
              border: "1px solid var(--border)",
              background: "var(--surface-elevated)",
              color: "var(--text-primary)",
              padding: "0.6rem 0.85rem",
              cursor: companyLoading || !companyInput.trim() ? "not-allowed" : "pointer",
              opacity: companyLoading || !companyInput.trim() ? 0.5 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
            }}
          >
            {companyLoading ? (
              <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
            ) : (
              <ArrowRight size={16} />
            )}
          </button>
        </section>

        {companyLoading && !companyResults && (
          <section
            className="surface-card"
            style={{
              padding: "1.2rem",
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
            }}
          >
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite", color: "var(--text-secondary)" }} />
            <span style={{ color: "var(--text-secondary)" }}>Searching by company...</span>
          </section>
        )}

        {companyResults && companyResults.length > 0 && (
          <section className="surface-card" style={{ padding: "1.2rem", display: "grid", gap: "0.6rem", opacity: companyLoading ? 0.6 : 1, transition: "opacity 0.15s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: "#74d39d",
                }}
              />
              <strong>
                {companyTotal != null && companyTotal > PAGE_SIZE
                  ? `Showing ${companyPage * PAGE_SIZE + 1}\u2013${Math.min((companyPage + 1) * PAGE_SIZE, companyTotal)} of ${companyTotal}`
                  : `${companyResults.length} match${companyResults.length !== 1 ? "es" : ""}`}
              </strong>
              <span style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>
                for &ldquo;{companyInput.trim()}&rdquo;
              </span>
            </div>

            <div style={{ display: "grid", gap: "0.35rem" }}>
              {companyResults.map((r) => (
                <button
                  key={r.candidate_id}
                  type="button"
                  onClick={() => router.push(`/profile/${r.candidate_id}?returnTo=/lookup`)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.65rem 0.75rem",
                    borderRadius: "10px",
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  <Building2 size={16} style={{ color: "var(--text-secondary)" }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.display_name}
                    </div>
                    <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.matched_company || r.primary_company}
                      {r.is_current && (
                        <span style={{ marginLeft: "0.4rem", color: "#74d39d", fontSize: "0.78rem" }}>current</span>
                      )}
                      {r.primary_headline && (
                        <span style={{ marginLeft: "0.4rem", opacity: 0.7 }}> · {r.primary_headline}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight size={14} style={{ color: "var(--text-secondary)" }} />
                </button>
              ))}
            </div>

            {companyTotal != null && companyTotal > PAGE_SIZE && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", paddingTop: "0.4rem" }}>
                <button
                  type="button"
                  disabled={companyPage === 0 || companyLoading}
                  onClick={() => handleCompanyPageChange(companyPage - 1)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.25rem",
                    padding: "0.35rem 0.6rem", borderRadius: "8px",
                    border: "1px solid var(--border)", background: "var(--surface)",
                    color: companyPage === 0 ? "var(--text-secondary)" : "var(--text-primary)",
                    cursor: companyPage === 0 || companyLoading ? "not-allowed" : "pointer",
                    opacity: companyPage === 0 ? 0.4 : 1, fontSize: "0.82rem",
                  }}
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                  {companyPage + 1} / {Math.ceil(companyTotal / PAGE_SIZE)}
                </span>
                <button
                  type="button"
                  disabled={(companyPage + 1) * PAGE_SIZE >= companyTotal || companyLoading}
                  onClick={() => handleCompanyPageChange(companyPage + 1)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.25rem",
                    padding: "0.35rem 0.6rem", borderRadius: "8px",
                    border: "1px solid var(--border)", background: "var(--surface)",
                    color: (companyPage + 1) * PAGE_SIZE >= companyTotal ? "var(--text-secondary)" : "var(--text-primary)",
                    cursor: (companyPage + 1) * PAGE_SIZE >= companyTotal || companyLoading ? "not-allowed" : "pointer",
                    opacity: (companyPage + 1) * PAGE_SIZE >= companyTotal ? 0.4 : 1, fontSize: "0.82rem",
                  }}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </section>
        )}

        {companyError && (
          <section
            className="surface-card"
            style={{
              padding: "1.2rem",
              display: "grid",
              gap: "0.6rem",
              borderColor: "color-mix(in srgb, var(--accent-red) 30%, var(--border) 70%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: companyError.startsWith("No matches") ? "var(--text-secondary)" : "#ef8f8f",
                }}
              />
              <strong>{companyError.startsWith("No matches") ? "No results" : "Request failed"}</strong>
            </div>
            <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.55 }}>
              {companyError}
            </p>
          </section>
        )}

        {/* Education Search Section */}
        <div style={{ display: "grid", gap: "0.5rem", marginTop: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <GraduationCap size={20} />
            <h2
              style={{
                margin: 0,
                fontFamily: '"AlisterHero", "AlisterSourceSans", sans-serif',
                fontSize: "1.15rem",
              }}
            >
              Education Search
            </h2>
          </div>
          <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.5, fontSize: "0.9rem" }}>
            Find people who attended a specific school or university.
          </p>
        </div>

        <section
          className="surface-card"
          style={{ padding: "0.85rem", display: "flex", gap: "0.5rem" }}
        >
          <input
            value={eduInput}
            onChange={(event) => setEduInput(event.target.value)}
            onKeyDown={handleEduKeyDown}
            placeholder="e.g. CVUT, MIT, Stanford"
            disabled={eduLoading}
            style={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid var(--border)",
              background: "var(--surface)",
              color: "var(--text-primary)",
              padding: "0.6rem 0.7rem",
            }}
          />
          <button
            type="button"
            onClick={handleEducationSearch}
            disabled={eduLoading || !eduInput.trim() || eduInput.trim().length < 2}
            style={{
              borderRadius: "10px",
              border: "1px solid var(--border)",
              background: "var(--surface-elevated)",
              color: "var(--text-primary)",
              padding: "0.6rem 0.85rem",
              cursor: eduLoading || !eduInput.trim() ? "not-allowed" : "pointer",
              opacity: eduLoading || !eduInput.trim() ? 0.5 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
            }}
          >
            {eduLoading ? (
              <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
            ) : (
              <ArrowRight size={16} />
            )}
          </button>
        </section>

        {eduLoading && !eduResults && (
          <section
            className="surface-card"
            style={{
              padding: "1.2rem",
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
            }}
          >
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite", color: "var(--text-secondary)" }} />
            <span style={{ color: "var(--text-secondary)" }}>Searching by education...</span>
          </section>
        )}

        {eduResults && eduResults.length > 0 && (
          <section className="surface-card" style={{ padding: "1.2rem", display: "grid", gap: "0.6rem", opacity: eduLoading ? 0.6 : 1, transition: "opacity 0.15s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: "#74d39d",
                }}
              />
              <strong>
                {eduTotal != null && eduTotal > PAGE_SIZE
                  ? `Showing ${eduPage * PAGE_SIZE + 1}\u2013${Math.min((eduPage + 1) * PAGE_SIZE, eduTotal)} of ${eduTotal}`
                  : `${eduResults.length} match${eduResults.length !== 1 ? "es" : ""}`}
              </strong>
              <span style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>
                for &ldquo;{eduInput.trim()}&rdquo;
              </span>
            </div>

            <div style={{ display: "grid", gap: "0.35rem" }}>
              {eduResults.map((r) => (
                <button
                  key={r.candidate_id}
                  type="button"
                  onClick={() => router.push(`/profile/${r.candidate_id}?returnTo=/lookup`)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.65rem 0.75rem",
                    borderRadius: "10px",
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  <GraduationCap size={16} style={{ color: "var(--text-secondary)" }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.display_name}
                    </div>
                    <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.matched_school}
                      {r.primary_headline && (
                        <span style={{ marginLeft: "0.4rem", opacity: 0.7 }}> · {r.primary_headline}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight size={14} style={{ color: "var(--text-secondary)" }} />
                </button>
              ))}
            </div>

            {eduTotal != null && eduTotal > PAGE_SIZE && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", paddingTop: "0.4rem" }}>
                <button
                  type="button"
                  disabled={eduPage === 0 || eduLoading}
                  onClick={() => handleEduPageChange(eduPage - 1)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.25rem",
                    padding: "0.35rem 0.6rem", borderRadius: "8px",
                    border: "1px solid var(--border)", background: "var(--surface)",
                    color: eduPage === 0 ? "var(--text-secondary)" : "var(--text-primary)",
                    cursor: eduPage === 0 || eduLoading ? "not-allowed" : "pointer",
                    opacity: eduPage === 0 ? 0.4 : 1, fontSize: "0.82rem",
                  }}
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                  {eduPage + 1} / {Math.ceil(eduTotal / PAGE_SIZE)}
                </span>
                <button
                  type="button"
                  disabled={(eduPage + 1) * PAGE_SIZE >= eduTotal || eduLoading}
                  onClick={() => handleEduPageChange(eduPage + 1)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.25rem",
                    padding: "0.35rem 0.6rem", borderRadius: "8px",
                    border: "1px solid var(--border)", background: "var(--surface)",
                    color: (eduPage + 1) * PAGE_SIZE >= eduTotal ? "var(--text-secondary)" : "var(--text-primary)",
                    cursor: (eduPage + 1) * PAGE_SIZE >= eduTotal || eduLoading ? "not-allowed" : "pointer",
                    opacity: (eduPage + 1) * PAGE_SIZE >= eduTotal ? 0.4 : 1, fontSize: "0.82rem",
                  }}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </section>
        )}

        {eduError && (
          <section
            className="surface-card"
            style={{
              padding: "1.2rem",
              display: "grid",
              gap: "0.6rem",
              borderColor: "color-mix(in srgb, var(--accent-red) 30%, var(--border) 70%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: eduError.startsWith("No matches") ? "var(--text-secondary)" : "#ef8f8f",
                }}
              />
              <strong>{eduError.startsWith("No matches") ? "No results" : "Request failed"}</strong>
            </div>
            <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.55 }}>
              {eduError}
            </p>
          </section>
        )}
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
